import { compute, ExpressionError, parseExpression, variablesUsed } from './expression';
import { makeRng, newSeed, pick, randFloat, randInt, type Rng } from './random';
import { countSigFigs, roundToSigFigs, type RuleInput, type SigfigRule } from './sigfigs';
import type { GeneratedProblem } from './problem';
import type { Difficulty, ProblemTemplate, VariableSpec } from './types';

/**
 * Imported problem templates: checked on import, then turned into problems.
 *
 * Templates are data. Nothing here evaluates them as code — every expression
 * goes through the restricted parser, and a template that cannot be satisfied
 * is rejected by name and reason rather than failing later in front of him.
 */

export class TemplateError extends Error {}

export interface TemplateIssue {
  path: string;
  message: string;
}

const SAMPLES_FOR_VALIDATION = 60;
const MAX_ATTEMPTS_PER_SAMPLE = 80;
/** Beyond this a result is not a chemistry answer, it is a bug. */
const PLAUSIBLE_MAGNITUDE = 1e24;

// --------------------------------------------------------------- constraints

const COMPARISONS = ['<=', '>=', '==', '!=', '<', '>'] as const;

/**
 * Constraints are the one place a comparison is allowed. Keeping them out of
 * the main grammar means an imported `compute` can never smuggle one in.
 */
export function checkConstraint(constraint: string, scope: Record<string, number | string>): boolean {
  for (const op of COMPARISONS) {
    const at = constraint.indexOf(op);
    if (at === -1) continue;
    const left = constraint.slice(0, at).trim();
    const right = constraint.slice(at + op.length).trim();
    if (!left || !right) throw new TemplateError(`"${constraint}" is not a complete comparison.`);
    const a = compute(left, scope);
    const b = compute(right, scope);
    switch (op) {
      case '<=':
        return a <= b;
      case '>=':
        return a >= b;
      case '==':
        return a === b;
      case '!=':
        return a !== b;
      case '<':
        return a < b;
      case '>':
        return a > b;
    }
  }
  throw new TemplateError(`"${constraint}" has no comparison in it.`);
}

// ----------------------------------------------------------------- sampling

interface Sampled {
  scope: Record<string, number | string>;
  /** How each numeric variable was written, for the significant-figure rule. */
  written: Map<string, string>;
}

function sampleOnce(variables: Record<string, VariableSpec>, rng: Rng): Sampled {
  const scope: Record<string, number | string> = {};
  const written = new Map<string, string>();

  for (const [name, spec] of Object.entries(variables)) {
    switch (spec.type) {
      case 'float': {
        const drawn = randFloat(rng, spec.min, spec.max, spec.sigfigs);
        scope[name] = drawn.value;
        written.set(name, drawn.text);
        break;
      }
      case 'int': {
        const value = randInt(rng, spec.min, spec.max);
        scope[name] = value;
        written.set(name, String(value));
        break;
      }
      case 'choice': {
        const value = pick(rng, spec.values);
        scope[name] = value;
        if (typeof value === 'number') written.set(name, String(value));
        break;
      }
      case 'choiceSet': {
        // Each field becomes its own variable, so related values stay together.
        const row = pick(rng, spec.values);
        for (const field of spec.fields) {
          const value = row[field];
          if (value === undefined) {
            throw new TemplateError(`A choiceSet row is missing the field "${field}".`);
          }
          scope[field] = value;
          if (typeof value === 'number') written.set(field, String(value));
        }
        break;
      }
    }
  }

  return { scope, written };
}

function sampleSatisfying(
  template: Pick<ProblemTemplate, 'variables' | 'constraints'>,
  rng: Rng,
): Sampled {
  for (let attempt = 0; attempt < MAX_ATTEMPTS_PER_SAMPLE; attempt += 1) {
    const sampled = sampleOnce(template.variables, rng);
    const ok = (template.constraints ?? []).every((c) => checkConstraint(c, sampled.scope));
    if (ok) return sampled;
  }
  throw new TemplateError('The constraints could not be satisfied after many tries.');
}

// -------------------------------------------------------------- validation

/** `{mass}` and `{molarMass(reactant)}` inside prompts and steps. */
function bracedExpressions(text: string): string[] {
  return [...text.matchAll(/\{([^{}]+)\}/g)].map((m) => m[1]!.trim());
}

export function validateTemplate(template: ProblemTemplate): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  const at = (field: string) => `problemTemplates[${template.id}].${field}`;

  const declared = new Set<string>();
  for (const [name, spec] of Object.entries(template.variables ?? {})) {
    if (spec.type === 'choiceSet') {
      for (const field of spec.fields) declared.add(field);
      spec.values.forEach((row, i) => {
        for (const field of spec.fields) {
          if (row[field] === undefined) {
            issues.push({
              path: at(`variables.${name}.values[${i}]`),
              message: `is missing the field "${field}"`,
            });
          }
        }
      });
    } else {
      declared.add(name);
    }

    if (spec.type === 'float' || spec.type === 'int') {
      if (spec.min > spec.max) {
        issues.push({ path: at(`variables.${name}`), message: 'has min greater than max' });
      }
    }
  }

  // Every expression must parse, and every name it uses must be declared.
  const checkExpression = (source: string, path: string) => {
    try {
      const node = parseExpression(source);
      for (const name of variablesUsed(node)) {
        if (!declared.has(name)) {
          issues.push({ path, message: `uses "${name}", which is not a declared variable` });
        }
      }
    } catch (err) {
      issues.push({ path, message: (err as Error).message });
    }
  };

  checkExpression(template.compute, at('compute'));

  for (const [i, constraint] of (template.constraints ?? []).entries()) {
    const op = COMPARISONS.find((o) => constraint.includes(o));
    if (!op) {
      issues.push({ path: at(`constraints[${i}]`), message: 'has no comparison in it' });
      continue;
    }
    const cut = constraint.indexOf(op);
    checkExpression(constraint.slice(0, cut), at(`constraints[${i}]`));
    checkExpression(constraint.slice(cut + op.length), at(`constraints[${i}]`));
  }

  for (const [field, text] of [['prompt', template.prompt] as const]) {
    for (const source of bracedExpressions(text)) {
      checkExpression(source, at(field));
    }
  }
  (template.steps ?? []).forEach((step, i) => {
    for (const source of bracedExpressions(step)) checkExpression(source, at(`steps[${i}]`));
  });

  // Stop before sampling if the template is already broken.
  if (issues.length > 0) return issues;

  let satisfied = 0;
  const rng = makeRng(`validate:${template.id}`);
  for (let i = 0; i < SAMPLES_FOR_VALIDATION; i += 1) {
    let sampled: Sampled;
    try {
      sampled = sampleSatisfying(template, rng);
    } catch (err) {
      issues.push({ path: at('constraints'), message: (err as Error).message });
      break;
    }
    satisfied += 1;

    try {
      const value = compute(template.compute, sampled.scope);
      if (!Number.isFinite(value)) {
        issues.push({ path: at('compute'), message: 'works out to an impossible number for some values' });
        break;
      }
      if (Math.abs(value) > PLAUSIBLE_MAGNITUDE) {
        issues.push({
          path: at('compute'),
          message: `works out to ${value.toExponential(2)} for some values, which is not a plausible answer`,
        });
        break;
      }
    } catch (err) {
      const message = err instanceof ExpressionError ? err.message : (err as Error).message;
      issues.push({ path: at('compute'), message: `fails for some values: ${message}` });
      break;
    }

    // The wording has to render too, not just the answer.
    try {
      renderText(template.prompt, sampled.scope);
      for (const step of template.steps ?? []) renderText(step, sampled.scope);
    } catch (err) {
      issues.push({ path: at('prompt'), message: (err as Error).message });
      break;
    }
  }

  if (satisfied === 0 && issues.length === 0) {
    issues.push({ path: at('variables'), message: 'produced no usable values' });
  }

  return issues;
}

// ----------------------------------------------------------- instantiation

/** Formats a computed value with a sensible number of figures. */
function formatValue(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  if (Number.isInteger(value) && Math.abs(value) < 1e6) return String(value);
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && (magnitude < 1e-3 || magnitude >= 1e6)) return value.toExponential(3);
  return roundToSigFigs(value, 4);
}

/** Replaces every `{…}` with a variable's value or an expression's result. */
export function renderText(text: string, scope: Record<string, number | string>): string {
  return text.replace(/\{([^{}]+)\}/g, (_, raw: string) => {
    const source = raw.trim();
    // A bare variable holding text is substituted as it stands.
    if (Object.hasOwn(scope, source) && typeof scope[source] === 'string') {
      return String(scope[source]);
    }
    const value = compute(source, scope);
    return formatValue(value);
  });
}

export function instantiate(template: ProblemTemplate, seed: string = newSeed()): GeneratedProblem {
  const rng = makeRng(`${template.id}:${seed}`);
  const sampled = sampleSatisfying(template, rng);
  const answerValue = compute(template.compute, sampled.scope);

  // Only the numbers that were actually written into the prompt can limit the
  // answer's significant figures. Atomic masses are exact by the spec's rule.
  const ruleInputs: RuleInput[] = [];
  for (const [name, text] of sampled.written) {
    if (template.prompt.includes(`{${name}}`)) ruleInputs.push({ text });
  }
  if (ruleInputs.length === 0) {
    for (const text of sampled.written.values()) ruleInputs.push({ text });
  }

  return {
    sourceId: template.id,
    seed,
    category: template.category,
    difficulty: template.difficulty as Difficulty,
    prompt: renderText(template.prompt, sampled.scope),
    answerValue,
    answerUnits: template.answerUnits,
    unitAliases: template.unitAliases ?? [],
    sigfigRule: template.sigfigRule as SigfigRule,
    ruleInputs,
    tolerance: template.tolerance ?? { relative: 0.01 },
    steps: (template.steps ?? []).map((step) => renderText(step, sampled.scope)),
  };
}

/** Used by the import screen to report on a whole block at once. */
export function validateTemplates(templates: ProblemTemplate[]): TemplateIssue[] {
  return templates.flatMap(validateTemplate);
}

export { countSigFigs };
